import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useAgenticConversation, useChatbotConversation, useEndAgenticSession, useEndOldChatbotSession } from '@components/Copilot/hooks';
import {
  AgentId,
  ChatMessage,
  GenerateChatbotSummaryData,
  GenerateChatbotSummaryRequest,
  PublishChatbotMessageData,
  PublishChatbotMessageRequest,
  TCopilotError,
  TSessionProps,
} from '@components/Copilot/types';
import { asyncNoop, noop, QueryKey } from '@fabric-ui/utils';
import { UseMutateFunction, useQueryClient } from '@tanstack/react-query';

const defaultLastSessionProps: TSessionProps = {
  agentId: AgentId.TICKET_AGENT,
  additionalParams: {
    entityKey: '',
    projectionId: { name: '', builtIn: true },
  },
  actions: [],
  getConfirmDialogConfig: undefined,
};

export interface IAvCopilotContext {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  sessionId: string;
  setSessionId: React.Dispatch<React.SetStateAction<string>>;
  threadId: string;
  setThreadId: React.Dispatch<React.SetStateAction<string>>;
  restartCopilotSession: () => Promise<void>;
  endCopilotSession: () => void;
  forceDiscardCopilot: () => void;
  closeCopilotWithBlocker: () => void;
  messages: ChatMessage[];
  addMessage: (message: ChatMessage) => void;
  postMessage: (message: { sessionId: string; message: string }) => void;
  publishPrompt: UseMutateFunction<PublishChatbotMessageData, Error, PublishChatbotMessageRequest, unknown>;
  summarizeMessage: UseMutateFunction<GenerateChatbotSummaryData, Error, GenerateChatbotSummaryRequest, unknown>;
  isPollingEnabled: boolean;
  isSummarizing: boolean;
  conversationErrorMessage: TCopilotError | null;
  setConversationErrorMessage: React.Dispatch<React.SetStateAction<TCopilotError | null>>;
  onOpenCopilot: (props: TSessionProps) => void;
  lastSessionProps: TSessionProps;
  setAgentId: (agentId: AgentId) => void;
  isDirty: boolean;
  isNavigationBlockerOpen: boolean;
  setIsNavigationBlockerOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const AvCopilotContext = React.createContext<IAvCopilotContext>({
  isOpen: false,
  setIsOpen: noop,
  sessionId: '',
  setSessionId: noop,
  threadId: '',
  setThreadId: noop,
  restartCopilotSession: asyncNoop,
  endCopilotSession: noop,
  forceDiscardCopilot: noop,
  closeCopilotWithBlocker: noop,
  messages: [],
  addMessage: noop,
  postMessage: noop,
  publishPrompt: noop,
  isPollingEnabled: false,
  summarizeMessage: noop,
  isSummarizing: false,
  conversationErrorMessage: null,
  setConversationErrorMessage: noop,
  onOpenCopilot: noop,
  lastSessionProps: defaultLastSessionProps,
  setAgentId: noop,
  isDirty: false,
  isNavigationBlockerOpen: false,
  setIsNavigationBlockerOpen: noop,
});

export function AvCopilotContextProvider({ children }: React.PropsWithChildren = {}) {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isNavigationBlockerOpen, setIsNavigationBlockerOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const [threadId, setThreadId] = useState<string>('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationErrorMessage, setConversationErrorMessage] = useState<TCopilotError | null>(null);
  const [lastSessionProps, setLastSessionProps] = useState<TSessionProps>(defaultLastSessionProps);

  const isOldImplementation = lastSessionProps.agentId === AgentId.TICKET_AGENT;

  const isDirty = useMemo(
    () => messages.length > 1 && !!lastSessionProps.actions.length && !!lastSessionProps.getConfirmDialogConfig,
    [lastSessionProps.actions.length, lastSessionProps.getConfirmDialogConfig, messages.length]
  );
  const chatRunInfo = useMemo(() => ({ threadId }), [threadId]);

  const { mutate: endSession } = useEndAgenticSession();
  const { mutate: endOldSession } = useEndOldChatbotSession();

  const resetCopilotState = useCallback(() => {
    setSessionId('');
    setThreadId('');
    setMessages([]);
    setConversationErrorMessage(null);
  }, []);

  const endCopilotSession = useCallback(() => {
    if (isOldImplementation) {
      if (chatRunInfo.threadId) {
        resetCopilotState();
        endOldSession({ chatRunInfo });
      }
    } else if (sessionId) {
      resetCopilotState();
      endSession({ sessionId });
    }
  }, [sessionId, threadId, isOldImplementation, resetCopilotState, endSession, endOldSession]);

  const onOpenCopilot = useCallback(
    (sessionProps: TSessionProps) => {
      const isOldImplementation = sessionProps.agentId === AgentId.TICKET_AGENT;
      if (!isOldImplementation || (sessionProps?.additionalParams?.entityKey && sessionProps?.additionalParams?.projectionId)) {
        setIsOpen(true);
        setLastSessionProps(sessionProps);
      }

      if (isOldImplementation) {
        if (chatRunInfo.threadId) {
          endCopilotSession();
        } else if (lastSessionProps) {
          resetCopilotState();
        }
      } else if (sessionId) {
        endCopilotSession();
      } else if (lastSessionProps) {
        resetCopilotState();
      }
    },
    [sessionId, threadId, isOldImplementation, lastSessionProps, endCopilotSession, resetCopilotState]
  );

  const restartCopilotSession = useCallback(async () => {
    if (isOldImplementation) {
      if (threadId) {
        endCopilotSession();
      } else {
        resetCopilotState();
      }
      await queryClient.refetchQueries({ queryKey: [QueryKey.Copilot, QueryKey.InitChatbotSession] });
    } else {
      if (sessionId) {
        endCopilotSession();
      }
      resetCopilotState();
    }
  }, [isOldImplementation, sessionId, threadId, endCopilotSession, resetCopilotState, queryClient]);

  const addMessage = useCallback((newMessage: ChatMessage) => setMessages(prevMessages => [...prevMessages, newMessage]), []);

  const agenticConversation = useAgenticConversation({
    isCopilotOpen: isOpen && !isOldImplementation,
    sessionId,
    addMessage,
    setErrorMessage: setConversationErrorMessage,
    setSessionId,
    lastSessionProps,
  });

  const chatbotConversation = useChatbotConversation({
    isCopilotOpen: isOpen && isOldImplementation,
    chatRunInfo: { threadId },
    addMessage,
    setErrorMessage: setConversationErrorMessage,
  });

  const forceDiscardCopilot = useCallback(() => {
    setIsOpen(false);
    resetCopilotState();
    queryClient.removeQueries({ queryKey: [QueryKey.Copilot] });
    if (isOldImplementation) {
      if (threadId) {
        endOldSession({ chatRunInfo: { threadId } });
      }
    } else if (sessionId) {
      endSession({ sessionId });
    }
  }, [endSession, endOldSession, resetCopilotState, sessionId, threadId, isOldImplementation]);

  const closeCopilotWithBlocker = useCallback(() => {
    if (isDirty) {
      setIsNavigationBlockerOpen(true);
    } else {
      forceDiscardCopilot();
    }
  }, [isDirty, forceDiscardCopilot]);

  const setAgentId = useCallback(
    (agentId: AgentId) => {
      setLastSessionProps(prev => ({ ...prev, agentId }));
      endCopilotSession();
    },
    [endCopilotSession]
  );

  const value = useMemo(
    () => ({
      isOpen,
      setIsOpen,
      sessionId,
      setSessionId,
      threadId,
      setThreadId,
      restartCopilotSession,
      endCopilotSession,
      forceDiscardCopilot,
      closeCopilotWithBlocker,
      messages,
      addMessage,
      postMessage: agenticConversation.postMessage,
      publishPrompt: chatbotConversation.publishMessage,
      summarizeMessage: chatbotConversation.summarizeMessage,
      isPollingEnabled: isOldImplementation ? chatbotConversation.isPollingEnabled : agenticConversation.isPollingEnabled,
      isSummarizing: chatbotConversation.isSummarizing,
      conversationErrorMessage,
      setConversationErrorMessage,
      onOpenCopilot,
      lastSessionProps,
      setAgentId,
      isDirty,
      isNavigationBlockerOpen,
      setIsNavigationBlockerOpen,
    }),
    [
      isOpen,
      setIsOpen,
      sessionId,
      setSessionId,
      threadId,
      setThreadId,
      restartCopilotSession,
      endCopilotSession,
      forceDiscardCopilot,
      closeCopilotWithBlocker,
      messages,
      addMessage,
      agenticConversation.postMessage,
      chatbotConversation.publishMessage,
      chatbotConversation.summarizeMessage,
      isOldImplementation,
      chatbotConversation.isPollingEnabled,
      agenticConversation.isPollingEnabled,
      chatbotConversation.isSummarizing,
      conversationErrorMessage,
      setConversationErrorMessage,
      onOpenCopilot,
      lastSessionProps,
      setAgentId,
      isDirty,
      isNavigationBlockerOpen,
      setIsNavigationBlockerOpen,
    ]
  );

  return <AvCopilotContext.Provider value={value}>{children}</AvCopilotContext.Provider>;
}

export const useAvCopilotContext = () => useContext(AvCopilotContext);
